// admin-ui/src/features/c/policies/fieldPolicies.ts

//Minimaländerung

export type UserRole =
  | 'guest'
  | 'user'
  | 'owner'
  | 'admin';

export type FieldPolicyMode =
  | 'admin_only'
  | 'owner_edit'
  | 'owner_proposal'
  | 'user_proposal'
  | 'readonly'
  | 'hidden';

export type FieldPolicyResult = {
  mode: FieldPolicyMode;
  canView: boolean;
  canEditDirectly: boolean;
  canSubmitProposal: boolean;
  requiresLogin: boolean;
  requiresReview: boolean;
};

export const LOCATION_FIELD_POLICIES: Record<string, FieldPolicyMode> = {
  // System
  id: 'readonly',
  created_at: 'readonly',
  updated_at: 'readonly',
  last_editor: 'readonly',
  last_edited_at: 'readonly',

  // Basisdaten
  display_name: 'owner_edit',
  name: 'hidden',

  name_de: 'owner_edit',
  name_en: 'owner_edit',
  name_it: 'owner_edit',
  name_fr: 'owner_edit',
  name_hr: 'owner_edit',

  description_de: 'owner_proposal',
  description_en: 'owner_proposal',
  description_it: 'owner_proposal',
  description_fr: 'owner_proposal',
  description_hr: 'owner_proposal',

  // Redaktion / Sichtbarkeit
  category_id: 'admin_only',
  active: 'admin_only',
  visibility: 'admin_only',
  editorial_status: 'admin_only',
  editorial_note: 'admin_only',
  is_draft: 'admin_only',
  to_review: 'admin_only',

  // Position
  lat: 'admin_only',
  lng: 'admin_only',
  geom: 'readonly',
  geometry: 'readonly',
  address: 'owner_proposal',
  plus_code: 'readonly',
  google_place_id: 'readonly',

  // Kontakt
  phone: 'owner_edit',
  website: 'owner_edit',
  email: 'owner_edit',
  reservation_link: 'owner_edit',

  // Externe / bewertende Daten
  maps_url: 'readonly',
  source: 'readonly',
  rating: 'readonly',
  user_ratings_total: 'readonly',
  price_level: 'readonly',

  // Sicherheit / Wind
  safety_wind_from: 'owner_proposal',
  safety_wind_to: 'owner_proposal',
};

export const ATTRIBUTE_KEY_POLICIES: Record<string, FieldPolicyMode> = {
  // Kontakt / Links
  website: 'owner_edit',
  formatted_phone_number: 'owner_edit',
  international_phone_number: 'owner_edit',
  url: 'readonly',

  // Öffnungszeiten
  'opening_hours.open_now': 'readonly',
  'opening_hours.weekday_text': 'owner_proposal',
  'opening_hours.periods': 'owner_proposal',
  'opening_hours Sonntag': 'owner_edit',
  'opening_hours Montag': 'owner_edit',
  'opening_hours Dienstag': 'owner_edit',
  'opening_hours Mittwoch': 'owner_edit',
  'opening_hours Donnerstag': 'owner_edit',
  'opening_hours Freitag': 'owner_edit',
  'opening_hours Samstag': 'owner_edit',
  permanently_closed: 'owner_proposal',

  // Ausstattung / Services
  'Kreditkartenzahlung möglich': 'owner_edit',
  'WLAN verfügbar': 'owner_edit',
  'Außenbereich verfügbar': 'owner_edit',

  // Google / externe Systemdaten
  place_id: 'readonly',
  plus_code: 'readonly',
  'plus_code.global_code': 'readonly',
  formatted_address: 'readonly',
  address_components: 'readonly',
  adr_address: 'readonly',
  vicinity: 'readonly',
  types: 'readonly',
  icon: 'readonly',

  // Bewertungen
  rating: 'readonly',
  user_ratings_total: 'readonly',
  reviews: 'readonly',
  price_level: 'readonly',

  // Geodaten
  'geometry.location.lat': 'readonly',
  'geometry.location.lng': 'readonly',
  'geometry.viewport.northeast.lat': 'readonly',
  'geometry.viewport.northeast.lng': 'readonly',
  'geometry.viewport.southwest.lat': 'readonly',
  'geometry.viewport.southwest.lng': 'readonly',
  utc_offset: 'readonly',

  // Fotos / externe Medien
  photos: 'readonly',

  // Redaktionelle Inhalte
  'editorial summary language': 'owner_proposal',
  'editorial summary overview': 'owner_proposal',

  // Wind2Horizon Fachfelder
  'LiveWind Station': 'admin_only',
  wind_hint: 'owner_proposal',
  swell_hint: 'owner_proposal',
};

export function getFieldPolicyMode(params: {
  scope: 'location' | 'attribute';
  key: string;
  locked?: boolean | null;
}): FieldPolicyMode {
  const { scope, key, locked } = params;

  if (locked) return 'readonly';

  if (scope === 'location') {
    return LOCATION_FIELD_POLICIES[key] ?? 'admin_only';
  }

  return ATTRIBUTE_KEY_POLICIES[key] ?? 'owner_proposal';
}

export function resolveFieldPolicy(params: {
  mode: FieldPolicyMode;
  role: UserRole;
}): FieldPolicyResult {
  const { mode, role } = params;

  if (mode === 'hidden') {
    return {
      mode,
      canView: false,
      canEditDirectly: false,
      canSubmitProposal: false,
      requiresLogin: false,
      requiresReview: false,
    };
  }

  if (mode === 'readonly') {
    return {
      mode,
      canView: true,
      canEditDirectly: false,
      canSubmitProposal: false,
      requiresLogin: false,
      requiresReview: false,
    };
  }

  if (mode === 'admin_only') {
    return {
      mode,
      canView: true,
      canEditDirectly: role === 'admin',
      canSubmitProposal: false,
      requiresLogin: role !== 'admin',
      requiresReview: false,
    };
  }

  if (mode === 'owner_edit') {
    return {
      mode,
      canView: true,
      canEditDirectly: role === 'owner' || role === 'admin',
      canSubmitProposal: role === 'guest' || role === 'user',
      requiresLogin: role === 'guest',
      requiresReview: role === 'guest' || role === 'user',
    };
  }

  if (mode === 'owner_proposal') {
    return {
      mode,
      canView: true,
      canEditDirectly: role === 'admin',
      canSubmitProposal: role === 'guest' || role === 'user' || role === 'owner',
      requiresLogin: role === 'guest',
      requiresReview: role !== 'admin',
    };
  }

  // user_proposal
  return {
    mode,
    canView: true,
    canEditDirectly: role === 'admin',
    canSubmitProposal: role === 'guest' || role === 'user' || role === 'owner',
    requiresLogin: role === 'guest',
    requiresReview: role !== 'admin',
  };
}

export function getFieldPolicy(params: {
  scope: 'location' | 'attribute';
  key: string;
  role: UserRole;
  locked?: boolean | null;
}): FieldPolicyResult {
  const mode = getFieldPolicyMode({
    scope: params.scope,
    key: params.key,
    locked: params.locked,
  });

  return resolveFieldPolicy({
    mode,
    role: params.role,
  });
}
export function getPolicyLabel(mode: FieldPolicyMode): string {
  switch (mode) {
    case 'admin_only':
      return 'Nur Redaktion'
    case 'owner_edit':
      return 'Direkt bearbeitbar'
    case 'owner_proposal':
      return 'Vorschlag erforderlich'
    case 'user_proposal':
      return 'Vorschlag möglich'
    case 'readonly':
      return 'Nur lesbar'
    case 'hidden':
      return 'Ausgeblendet'
    default:
      return 'Unbekannte Berechtigung'
  }
}

export function getLockedFieldStyle(params: {
  canEditDirectly: boolean
}) {
  const { canEditDirectly } = params

  return {
    background: canEditDirectly ? '#ffffff' : '#d1d5db',
    color: canEditDirectly ? '#111827' : '#374151',
    border: canEditDirectly ? '1px solid #d1d5db' : '1px solid #9ca3af',
    cursor: canEditDirectly ? 'text' : 'not-allowed',
    fontWeight: canEditDirectly ? 400 : 600,
  }
}

export function getPolicyHint(policy: FieldPolicyResult): string | null {
  if (!policy.canView) return null

  if (policy.canEditDirectly) return null

  if (policy.requiresLogin) {
    return '🔒 Login oder Berechtigung erforderlich'
  }

  if (policy.canSubmitProposal) {
    return '✏️ Änderung nur als Vorschlag möglich'
  }

  if (policy.mode === 'readonly') {
    return '🔒 Nur lesbar'
  }

  if (policy.mode === 'admin_only') {
    return '🔒 Nur Redaktion'
  }

  return '🔒 Nicht direkt bearbeitbar'
}